import React from 'react';
import {
  FaSearch,
  FaTimes,
  FaThLarge,
  FaList,
  FaFilter,
} from 'react-icons/fa';
import { Card } from './Primitives';
import { powerStateMeta, VIEW_MODE } from '../utils/constants';
import { hasActiveFilters } from '../utils/format';

/** Select compatta e accessibile per un filtro. */
function FilterSelect({ id, label, value, options, onChange, renderOption }) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1 text-xs">
      <span className="font-medium text-gray-500 dark:text-gray-400">{label}</span>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
      >
        <option value="">Tutti</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {renderOption ? renderOption(opt) : opt}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Barra filtri compatta e responsive: ricerca libera, filtri per ambiente /
 * prodotto / ruolo / stato, azzeramento e selettore vista (card/tabella).
 */
export default function FilterBar({
  filters,
  options,
  viewMode,
  onSetFilter,
  onReset,
  onSetViewMode,
  resultCount,
}) {
  const active = hasActiveFilters(filters);
  return (
    <Card className="p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          {/* Ricerca */}
          <div className="relative flex-1 lg:max-w-md">
            <label htmlFor="esxi-search" className="sr-only">
              Cerca per nome, IP, prodotto o ruolo
            </label>
            <FaSearch
              size={14}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="esxi-search"
              type="search"
              value={filters.search}
              onChange={(e) => onSetFilter('search', e.target.value)}
              placeholder="Cerca per nome, IP, prodotto o ruolo…"
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            />
          </div>

          {/* Selettore vista */}
          <div
            className="flex items-center gap-1 self-start rounded-lg border border-gray-200 p-1 dark:border-gray-600"
            role="group"
            aria-label="Modalità di visualizzazione"
          >
            <button
              type="button"
              onClick={() => onSetViewMode(VIEW_MODE.CARDS)}
              aria-pressed={viewMode === VIEW_MODE.CARDS}
              title="Vista a card"
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                viewMode === VIEW_MODE.CARDS
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <FaThLarge size={12} aria-hidden="true" /> Card
            </button>
            <button
              type="button"
              onClick={() => onSetViewMode(VIEW_MODE.TABLE)}
              aria-pressed={viewMode === VIEW_MODE.TABLE}
              title="Vista tabellare"
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                viewMode === VIEW_MODE.TABLE
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <FaList size={12} aria-hidden="true" /> Tabella
            </button>
          </div>
        </div>

        {/* Filtri a tendina */}
        <div className="flex flex-wrap items-end gap-3">
          <FilterSelect
            id="esxi-filter-env"
            label="Ambiente"
            value={filters.environment}
            options={options.environments}
            onChange={(v) => onSetFilter('environment', v)}
          />
          <FilterSelect
            id="esxi-filter-product"
            label="Prodotto"
            value={filters.product}
            options={options.products}
            onChange={(v) => onSetFilter('product', v)}
          />
          <FilterSelect
            id="esxi-filter-role"
            label="Ruolo"
            value={filters.role}
            options={options.roles}
            onChange={(v) => onSetFilter('role', v)}
          />
          <FilterSelect
            id="esxi-filter-state"
            label="Stato"
            value={filters.state}
            options={options.states}
            onChange={(v) => onSetFilter('state', v)}
            renderOption={(opt) => powerStateMeta(opt).label}
          />

          <button
            type="button"
            onClick={onReset}
            disabled={!active}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors motion-reduce:transition-none hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            title="Azzera tutti i filtri"
          >
            <FaTimes size={11} aria-hidden="true" /> Azzera filtri
          </button>

          <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <FaFilter size={11} aria-hidden="true" />
            {resultCount} VM {active ? 'filtrate' : 'totali'}
          </span>
        </div>
      </div>
    </Card>
  );
}
